import { ReactNode, useState, useEffect, useRef } from 'react';
import { Package } from 'lucide-react';
import AIChatWidget from '@/components/AIChatWidget';
import MessagingWidget from '@/components/messaging/MessagingWidget';
import { getGeneralSettings } from '@/lib/settingsStore';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/hooks/usePermissions';
import { NavLink } from '@/components/NavLink';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { LayoutDashboard, Globe, Settings, LogOut, PlusCircle, ClipboardCheck, Calculator, Truck, User, Users, AlertTriangle, KeyRound, ChevronUp, UserCog, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNavigate } from 'react-router-dom';
import ProfileSettingsDialog from '@/components/ProfileSettingsDialog';
import { supabase } from '@/integrations/supabase/client';

const AppLayout = ({ children }: { children: ReactNode }) => {
  const { t, lang, setLang, dir } = useLanguage();
  const { signOut, user, mustChangeCredentials } = useAuth();
  const { company } = useTenant();
  const { canAccessScreen, role } = usePermissions();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(() => getGeneralSettings());
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const name = user?.user_metadata?.name || user?.email || '';
  const initials = name
    ? name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  // Resolve logo: settings > tenant sidebar_logo > tenant logo > null
  const resolvedSidebarLogo = settings.sidebarLogo || settings.companyLogo || company?.sidebar_logo_url || company?.logo_url || null;

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('avatar_url')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => setAvatarUrl(data?.avatar_url || null));
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handler = () => setSettings(getGeneralSettings());
    window.addEventListener('general-settings-changed', handler);
    return () => window.removeEventListener('general-settings-changed', handler);
  }, []);

  const sidebarSide = dir === 'rtl' ? 'right' : 'left';

  const allNavItems = [
    { title: t('dashboard'), url: '/', icon: LayoutDashboard, screen: 'dashboard' },
    { title: t('entry'), url: '/entry', icon: PlusCircle, screen: 'entry' },
    { title: t('shipments'), url: '/shipments', icon: Package, screen: 'shipments' },
    { title: t('followUp'), url: '/follow-up', icon: ClipboardCheck, screen: 'followUp' },
    { title: t('accounting'), url: '/accounting', icon: Calculator, screen: 'accounting' },
    { title: t('driverApp'), url: '/driver-app', icon: Truck, screen: 'driverApp' },
    { title: t('customerApp'), url: '/customer-app', icon: User, screen: 'customerApp' },
    { title: t('customers'), url: '/customers', icon: Users, screen: 'customers' },
    { title: t('drivers'), url: '/drivers', icon: Truck, screen: 'drivers' },
    { title: lang === 'ar' ? 'المخزون' : 'Inventory', url: '/inventory', icon: Warehouse, screen: 'inventory' },
    { title: t('settings'), url: '/settings', icon: Settings, screen: 'settings' },
  ];

  const navItems = allNavItems.filter(item => canAccessScreen(item.screen));

  const [driverDarkHeader, setDriverDarkHeader] = useState(() => localStorage.getItem('driver-dark-mode') !== 'false');
  const [customerDarkHeader, setCustomerDarkHeader] = useState(() => localStorage.getItem('customer-dark-mode') !== 'false');
  useEffect(() => {
    const handler = () => {
      setDriverDarkHeader(localStorage.getItem('driver-dark-mode') !== 'false');
      setCustomerDarkHeader(localStorage.getItem('customer-dark-mode') !== 'false');
    };
    window.addEventListener('storage', handler);
    const interval = setInterval(() => {
      setDriverDarkHeader(localStorage.getItem('driver-dark-mode') !== 'false');
      setCustomerDarkHeader(localStorage.getItem('customer-dark-mode') !== 'false');
    }, 300);
    return () => { window.removeEventListener('storage', handler); clearInterval(interval); };
  }, []);

  if (role === 'driver' || role === 'customer') {
    const dm = role === 'driver' ? driverDarkHeader : role === 'customer' ? customerDarkHeader : false;
    return (
      <div className="flex min-h-screen flex-col" dir={dir}>
        <header className={`flex h-14 items-center justify-between border-b px-4 transition-colors duration-300 relative z-50 ${dm ? 'bg-[#111827] border-[#1e2a3a] text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
          <div className="flex items-center gap-2.5">
            {resolvedSidebarLogo ? (
              <img src={resolvedSidebarLogo} alt="Logo" className="max-h-10 max-w-[80px] object-contain" />
            ) : (
              <Package className="h-8 w-8 text-primary" />
            )}
            <Avatar className="h-8 w-8">
              <AvatarImage src={avatarUrl || undefined} alt={name} />
              <AvatarFallback className="text-xs bg-primary text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
            <span className={`text-sm font-medium truncate ${dm ? 'text-gray-200' : 'text-foreground'}`}>
              {lang === 'ar' ? `أهلاً، ${name}` : `Hello, ${name}`}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className={dm ? 'text-gray-300 hover:bg-[#1e2a3a]' : ''} onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}>
              <Globe className="h-4 w-4" />
            </Button>
            <div ref={userMenuRef} className="relative">
              {userMenuOpen && (
                <div className={`absolute top-full mt-1 end-0 z-50 rounded-md border shadow-lg overflow-hidden w-44 ${dm ? 'bg-[#111827] border-[#1e2a3a]' : 'bg-popover border-border'}`}>
                  <button
                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${dm ? 'text-gray-200 hover:bg-[#1e2a3a]' : 'hover:bg-muted'}`}
                    onClick={() => { setProfileDialogOpen(true); setUserMenuOpen(false); }}
                  >
                    <UserCog className={`h-4 w-4 shrink-0 ${dm ? 'text-gray-400' : 'text-muted-foreground'}`} />
                    <span>{lang === 'ar' ? 'إعدادات الملف الشخصي' : 'Profile Settings'}</span>
                  </button>
                  <button
                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${dm ? 'text-gray-200 hover:bg-[#1e2a3a]' : 'hover:bg-muted'}`}
                    onClick={() => { navigate('/change-credentials'); setUserMenuOpen(false); }}
                  >
                    <KeyRound className={`h-4 w-4 shrink-0 ${dm ? 'text-gray-400' : 'text-muted-foreground'}`} />
                    <span>{lang === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}</span>
                  </button>
                  <button
                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${dm ? 'text-red-400 hover:bg-[#1e2a3a]' : 'text-destructive hover:bg-muted'}`}
                    onClick={() => { signOut(); setUserMenuOpen(false); }}
                  >
                    <LogOut className="h-4 w-4 shrink-0" />
                    <span>{t('logout')}</span>
                  </button>
                </div>
              )}
              <Button variant="ghost" size="sm" className={dm ? 'text-gray-300 hover:bg-[#1e2a3a]' : ''} onClick={() => setUserMenuOpen(v => !v)}>
                <User className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>
        {mustChangeCredentials && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-amber-800 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{lang === 'ar' ? 'يرجى تغيير بيانات الدخول الخاصة بك.' : 'Please change your login credentials.'}</span>
            <a href="/change-credentials" className="underline font-medium hover:text-amber-900">
              {lang === 'ar' ? 'تغيير الآن' : 'Change now'}
            </a>
          </div>
        )}
        <main className="flex-1 p-4 md:p-6 relative overflow-hidden">
            {resolvedSidebarLogo && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-0" aria-hidden="true">
                <img src={resolvedSidebarLogo} alt="" className="w-[60%] max-w-[500px] opacity-[0.04] dark:opacity-[0.06] dark:invert select-none" draggable={false} />
              </div>
            )}
            <div className="relative z-10">{children}</div>
          </main>
        <ProfileSettingsDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} hideDarkMode />
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="flex min-h-screen w-full" dir={dir}>
        <Sidebar collapsible="icon" side={sidebarSide} className="glass">
          <div className="flex h-14 items-center justify-center border-b border-border/50 px-2 overflow-hidden">
            {resolvedSidebarLogo ? (
              <img
                src={resolvedSidebarLogo}
                alt="Logo"
                className="shrink-0 object-contain transition-all dark:brightness-0 dark:invert"
                style={{
                  maxHeight: `${((settings.sidebarLogoSize ?? 100) / 100) * 48}px`,
                  maxWidth: `${settings.sidebarLogoSize ?? 100}%`,
                }}
              />
            ) : (
              <Package className="h-8 w-8 text-primary shrink-0" />
            )}
          </div>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navItems.map(item => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink to={item.url} end className="hover:bg-muted/50 transition-all duration-200" activeClassName="bg-primary/10 text-primary font-medium sidebar-active-indicator">
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <div className="mt-auto border-t p-3 group-data-[collapsible=icon]:px-1 space-y-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 group-data-[collapsible=icon]:justify-center"
              onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
            >
              <Globe className="h-4 w-4 shrink-0" />
              <span className="group-data-[collapsible=icon]:hidden">
                {lang === 'ar' ? 'English' : 'العربية'}
              </span>
            </Button>

            <div ref={userMenuRef} className="relative">
              {userMenuOpen && (
                <div className="absolute bottom-full mb-1 start-0 end-0 z-50 rounded-md border bg-popover shadow-lg overflow-hidden group-data-[collapsible=icon]:w-44 group-data-[collapsible=icon]:start-8">
                  <button
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
                    onClick={() => { setProfileDialogOpen(true); setUserMenuOpen(false); }}
                  >
                    <UserCog className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>{lang === 'ar' ? 'إعدادات الملف الشخصي' : 'Profile Settings'}</span>
                  </button>
                  <button
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
                    onClick={() => { navigate('/change-credentials'); setUserMenuOpen(false); }}
                  >
                    <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>{lang === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}</span>
                  </button>
                  <button
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-muted transition-colors"
                    onClick={() => { signOut(); setUserMenuOpen(false); }}
                  >
                    <LogOut className="h-4 w-4 shrink-0" />
                    <span>{t('logout')}</span>
                  </button>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 group-data-[collapsible=icon]:justify-center"
                onClick={() => setUserMenuOpen(v => !v)}
              >
                <User className="h-4 w-4 shrink-0" />
                <span className="group-data-[collapsible=icon]:hidden flex-1 truncate text-start">
                  {user?.user_metadata?.name || user?.email || (lang === 'ar' ? 'المستخدم' : 'User')}
                </span>
                <ChevronUp className={`h-3 w-3 shrink-0 group-data-[collapsible=icon]:hidden transition-transform ${userMenuOpen ? '' : 'rotate-180'}`} />
              </Button>
            </div>
          </div>
        </Sidebar>

        <div className="flex flex-1 flex-col min-w-0">
          <header className="flex h-14 items-center gap-3 header-gradient-border px-4">
            <SidebarTrigger />
            
            {/* Company header branding (logo/text from branding_options) */}
            {(company?.branding_options as any)?.header_logo_url || (company?.branding_options as any)?.header_text ? (
              <div className="flex items-center gap-2 me-auto">
                {(company?.branding_options as any)?.header_logo_url && (
                  <img 
                    src={(company.branding_options as any).header_logo_url} 
                    alt={company?.name || ''} 
                    className="h-8 w-auto max-w-[140px] object-contain"
                  />
                )}
                {(company?.branding_options as any)?.header_text && (
                  <span className="text-sm font-bold text-primary truncate max-w-[200px]">
                    {(company.branding_options as any).header_text}
                  </span>
                )}
              </div>
            ) : null}

            <div className="flex items-center gap-2.5 ms-auto">
              <Avatar className="h-8 w-8">
                <AvatarImage src={avatarUrl || undefined} alt={name} />
                <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-foreground truncate">
                {lang === 'ar' ? `أهلاً، ${name}` : `Hello, ${name}`}
              </span>
            </div>
          </header>
          {mustChangeCredentials && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-amber-800 text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{lang === 'ar' ? 'يرجى تغيير بيانات الدخول الخاصة بك.' : 'Please change your login credentials.'}</span>
              <a href="/change-credentials" className="underline font-medium hover:text-amber-900">
                {lang === 'ar' ? 'تغيير الآن' : 'Change now'}
              </a>
            </div>
          )}
          <main className="flex-1 p-4 md:p-6 relative overflow-hidden">
            {resolvedSidebarLogo && (
              <div
                className="pointer-events-none absolute inset-0 flex items-center justify-center z-0"
                aria-hidden="true"
              >
                <img
                  src={resolvedSidebarLogo}
                  alt=""
                  className="w-[60%] max-w-[500px] opacity-[0.04] dark:opacity-[0.06] dark:invert select-none"
                  draggable={false}
                />
              </div>
            )}
            <div className="relative z-10">
              {children}
            </div>
          </main>
        </div>
      </div>
      {settings.messagingEnabled && <MessagingWidget />}
      {settings.aiAssistantEnabled && <AIChatWidget />}
      <ProfileSettingsDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />
    </SidebarProvider>
  );
};

export default AppLayout;
